import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import {
  getCurrentPushSubscription,
  getPushPermissionState,
  isIosPushInstallRequired,
  unsubscribeCurrentDeviceFromPush,
} from '../../lib/pushNotifications';
import { useAuthenticatedFetch } from '../../utils/apiClient';
import { deletePushSubscription } from '../playbook/api';
import {
  fetchNotificationPreferences,
  type NotificationPreferences,
  updateNotificationPreference,
} from './api';

type Props = {
  open: boolean;
  onClose: () => void;
};

const NotificationsSettingsModal: React.FC<Props> = ({ open, onClose }) => {
  const { authReady, isPremium, userEmail } = useAuth();
  const navigate = useNavigate();
  const authenticatedFetch = useAuthenticatedFetch();
  const authenticatedFetchRef = useRef(authenticatedFetch);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deviceBusy, setDeviceBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deviceMessage, setDeviceMessage] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<NotificationPreferences | null>(null);
  const [currentDeviceSubscribed, setCurrentDeviceSubscribed] = useState(false);

  useEffect(() => {
    authenticatedFetchRef.current = authenticatedFetch;
  }, [authenticatedFetch]);

  useEffect(() => {
    if (!open || !authReady || !userEmail || !isPremium) {
      return;
    }

    let active = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      setDeviceMessage(null);
      try {
        const [nextPreferences, currentSubscription] = await Promise.all([
          fetchNotificationPreferences(authenticatedFetchRef.current),
          getCurrentPushSubscription(),
        ]);

        if (!active) return;
        setPreferences(nextPreferences);
        setCurrentDeviceSubscribed(Boolean(currentSubscription));
      } catch (loadError) {
        if (!active) return;
        setError(loadError instanceof Error ? loadError.message : 'Failed to load notification settings');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    void load();

    return () => {
      active = false;
    };
  }, [authReady, isPremium, open, userEmail]);

  const pushPreference = preferences?.notifications.strategy_action_required.push ?? null;
  const iosInstallRequired = isIosPushInstallRequired();

  const handleTogglePush = async () => {
    if (!pushPreference) return;

    setSaving(true);
    setError(null);
    setDeviceMessage(null);
    try {
      const nextPreferences = await updateNotificationPreference(
        {
          notificationCategory: 'strategy_action_required',
          channel: 'push',
          enabled: !pushPreference.enabled,
        },
        authenticatedFetchRef.current
      );
      setPreferences(nextPreferences);
    } catch (updateError) {
      setError(updateError instanceof Error ? updateError.message : 'Failed to update notification settings');
    } finally {
      setSaving(false);
    }
  };

  const reloadSettings = async () => {
    const [nextPreferences, currentSubscription] = await Promise.all([
      fetchNotificationPreferences(authenticatedFetchRef.current),
      getCurrentPushSubscription(),
    ]);
    setPreferences(nextPreferences);
    setCurrentDeviceSubscribed(Boolean(currentSubscription));
  };

  const handleDisableCurrentDevice = async () => {
    setDeviceBusy(true);
    setError(null);
    setDeviceMessage(null);
    try {
      const { endpoint } = await unsubscribeCurrentDeviceFromPush();
      if (endpoint) {
        await deletePushSubscription(endpoint, getPushPermissionState(), authenticatedFetchRef.current);
      }
      await reloadSettings();
      setDeviceMessage('Push has been disabled on this device.');
    } catch (disableError) {
      setError(disableError instanceof Error ? disableError.message : 'Failed to disable push on this device');
      await reloadSettings().catch(() => undefined);
    } finally {
      setDeviceBusy(false);
    }
  };

  const handleOpenMyStrategies = () => {
    onClose();
    navigate('/strategies?tab=my_strategies');
  };

  if (!open || !authReady || !userEmail || !isPremium) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-start justify-center bg-black/40 px-4 py-8 sm:items-center">
      <div className="w-full max-w-[560px] rounded-[20px] border border-[var(--qcl-border)] bg-white shadow-[0_24px_80px_rgba(15,23,42,0.22)]">
        <div className="flex items-start justify-between gap-4 border-b border-[var(--qcl-border)] px-5 py-4">
          <div>
            <h2 className="text-[18px] font-bold tracking-[-0.02em] text-[var(--qcl-text)]">Notifications</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[10px] p-2 text-[var(--qcl-text-dim)] transition hover:bg-[var(--qcl-bg)] hover:text-[var(--qcl-text)]"
            aria-label="Close notifications settings"
          >
            ✕
          </button>
        </div>

        <div className="px-5 py-4">
          {loading ? (
            <div className="rounded-[14px] border border-[var(--qcl-border)] bg-[var(--qcl-bg)] p-4 text-[13px] text-[var(--qcl-text-dim)]">
              Loading notification settings...
            </div>
          ) : (
            <div className="rounded-[16px] border border-[var(--qcl-border)] bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="text-[14px] font-semibold text-[var(--qcl-text)]">Push Notifications for Strategies</div>
                  <div className="mt-1 text-[12px] text-[var(--qcl-text-dim)]">
                    Receive a push alert when one of your followed strategies has a trade you need to act on.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleTogglePush}
                  disabled={loading || saving || !pushPreference}
                  className={`inline-flex min-h-[36px] min-w-[92px] items-center justify-center rounded-full px-4 text-[12px] font-semibold transition ${
                    pushPreference?.enabled
                      ? 'bg-[var(--qcl-accent)] text-white hover:bg-[var(--qcl-accent-hover)]'
                      : 'border border-[var(--qcl-border)] bg-white text-[var(--qcl-text)] hover:bg-[var(--qcl-bg)]'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  {saving ? 'Saving...' : pushPreference?.enabled ? 'Push On' : 'Push Off'}
                </button>
              </div>

              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                <span className="rounded-full bg-[var(--qcl-bg)] px-2 py-1 text-[var(--qcl-text-dim)]">
                  Account: {pushPreference?.enabled ? 'on' : 'off'}
                </span>
                <span className="rounded-full bg-[var(--qcl-bg)] px-2 py-1 text-[var(--qcl-text-dim)]">
                  Ready devices: {pushPreference?.activeSubscriptionCount ?? 0}
                </span>
              </div>

              {!pushPreference?.pushAvailable ? (
                <div className="mt-3 text-[12px] text-[var(--qcl-amber,#e8930c)]">
                  {pushPreference?.availabilityReason || 'Push notifications are not enabled for this account yet.'}
                </div>
              ) : null}

              {iosInstallRequired ? (
                <div className="mt-3 text-[12px] text-[var(--qcl-amber,#e8930c)]">
                  On iPhone and iPad, push only works from the installed Home Screen app.
                </div>
              ) : null}

              {!currentDeviceSubscribed && pushPreference?.enabled ? (
                <div className="mt-4 rounded-[14px] border border-dashed border-[var(--qcl-border)] bg-[var(--qcl-bg)] p-3">
                  <div className="text-[12px] font-semibold text-[var(--qcl-text)]">This device still needs to be enabled</div>
                  <div className="mt-1 text-[12px] text-[var(--qcl-text-dim)]">
                    Keep push on for your account here, then open My Strategies and enable push on this phone or browser.
                  </div>
                  <button
                    type="button"
                    onClick={handleOpenMyStrategies}
                    className="mt-3 rounded-[8px] border border-[var(--qcl-border)] px-3 py-1.5 text-[12px] font-semibold text-[var(--qcl-text)] transition hover:bg-white"
                  >
                    Open My Strategies
                  </button>
                </div>
              ) : null}

              {currentDeviceSubscribed ? (
                <div className="mt-4 rounded-[14px] border border-[var(--qcl-border)] bg-[var(--qcl-bg)] p-3">
                  <div className="text-[12px] font-semibold text-[var(--qcl-text)]">This device has an active push subscription</div>
                  <div className="mt-1 text-[12px] text-[var(--qcl-text-dim)]">
                    Turn push off above to mute alerts account-wide, or manage this specific phone/browser below.
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleDisableCurrentDevice}
                      disabled={deviceBusy}
                      className="rounded-[8px] border border-[var(--qcl-border)] px-3 py-1.5 text-[12px] font-semibold text-[var(--qcl-text)] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {deviceBusy ? 'Turning Off...' : 'Disable on This Device'}
                    </button>
                  </div>
                </div>
              ) : null}

              {deviceMessage ? (
                <div className="mt-3 text-[12px] text-[var(--qcl-green,#06b68c)]">
                  {deviceMessage}
                </div>
              ) : null}
            </div>
          )}

          {error ? (
            <div className="mt-4 rounded-[12px] border border-[rgba(236,72,128,0.18)] bg-[rgba(255,240,246,0.9)] px-3 py-2 text-[12px] text-[var(--qcl-red,#ec4880)]">
              {error}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default NotificationsSettingsModal;
